import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const OG_IMAGE = '/og-cover.png';
const SITE = 'culturecareers.org';
const DEFAULT_TITLE = 'Arts and Culture Jobs in India | CultureCareers';
const DEFAULT_DESC =
  "Curated jobs, gigs, residencies, grants and open calls across India's arts, culture and design sector for artists, curators, designers and cultural pros.";

const Kicker = ({ children }) => (
  <p className="text-xs uppercase tracking-[0.25em] font-medium mb-3" style={{ color: '#D4820A' }}>
    {children}
  </p>
);

/* WhatsApp-style share card */
const WhatsAppMock = () => (
  <div
    className="rounded-2xl overflow-hidden shadow-sm"
    style={{ backgroundColor: '#DCF8C6', padding: '10px 10px 8px', maxWidth: 360 }}
  >
    <div className="rounded-xl overflow-hidden" style={{ backgroundColor: '#F0EBE2' }}>
      <img src={OG_IMAGE} alt="preview" className="w-full block" />
      <div className="px-3 py-2.5">
        <p className="text-[13px] font-medium leading-snug mb-1" style={{ color: '#0B141A' }}>
          {DEFAULT_TITLE}
        </p>
        <p className="text-[11.5px] leading-snug" style={{ color: '#0F2942' }}>
          {DEFAULT_DESC.slice(0, 100)}…
        </p>
        <p className="text-[11px] mt-1" style={{ color: '#8696A0' }}>{SITE}</p>
      </div>
    </div>
    <p className="text-[11px] mt-1.5 text-right" style={{ color: '#0F2942' }}>12:34 ✓✓</p>
  </div>
);

/* LinkedIn-style share card */
const LinkedInMock = () => (
  <div className="rounded-lg overflow-hidden border bg-white" style={{ borderColor: '#E0E0E0', maxWidth: 420 }}>
    <img src={OG_IMAGE} alt="preview" className="w-full block" />
    <div className="px-4 py-3" style={{ backgroundColor: '#F0EBE2' }}>
      <p className="text-[11px] uppercase tracking-wide mb-0.5" style={{ color: '#666' }}>{SITE}</p>
      <p className="text-[15px] font-semibold leading-snug" style={{ color: '#000' }}>
        {DEFAULT_TITLE}
      </p>
    </div>
  </div>
);

/* iMessage-style (minimal) */
const IMessageMock = () => (
  <div className="rounded-2xl overflow-hidden shadow-sm" style={{ backgroundColor: '#E9E9EB', maxWidth: 300 }}>
    <img src={OG_IMAGE} alt="preview" className="w-full block" />
    <div className="px-3 py-2.5">
      <p className="text-[13px] font-semibold leading-snug mb-0.5" style={{ color: '#000' }}>
        {DEFAULT_TITLE}
      </p>
      <p className="text-[11.5px] leading-snug" style={{ color: '#4A4A4F' }}>
        {DEFAULT_DESC.slice(0, 80)}…
      </p>
      <p className="text-[11px] mt-1 uppercase" style={{ color: '#8E8E93' }}>{SITE}</p>
    </div>
  </div>
);

const OGCoverPreview = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F0EBE2' }}>
      <SEO
        title="Share Preview"
        path="/og-cover"
        description="Preview of how CultureCareers links appear when shared on WhatsApp, LinkedIn, iMessage, and other platforms."
      />
      <Navbar />

      <section className="px-6 md:px-12 lg:px-24 py-16 md:py-20" style={{ backgroundColor: '#0F2942' }}>
        <div className="max-w-4xl">
          <h1 className="text-4xl md:text-5xl font-light tracking-tight leading-tight mb-4 text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Share preview
          </h1>
          <div className="w-16 h-px mb-6" style={{ backgroundColor: 'rgba(212,130,10,0.6)' }} />
          <p className="text-base md:text-lg leading-relaxed font-light max-w-2xl" style={{ color: 'rgba(240, 235, 226, 0.65)' }}>
            How a CultureCareers link looks when someone shares it on WhatsApp, LinkedIn, iMessage, or any platform that reads Open Graph tags.
          </p>
        </div>
      </section>

      {/* The raw OG image, full-bleed */}
      <section className="px-6 md:px-12 lg:px-24 py-16">
        <div className="max-w-5xl mx-auto">
          <Kicker>The image</Kicker>
          <h2 className="text-2xl md:text-3xl font-normal tracking-tight mb-6" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0F2942' }}>
            The OG cover (1200 × 630)
          </h2>
          <div className="rounded-xl overflow-hidden shadow-sm border" style={{ borderColor: '#F0EBE2' }}>
            <img src={OG_IMAGE} alt="CultureCareers OG cover" className="w-full block" />
          </div>
          <p className="text-sm mt-4" style={{ color: '#0F2942' }}>
            Served from <code className="px-1.5 py-0.5 rounded text-[13px]" style={{ backgroundColor: '#F0EBE2' }}>/og-cover.png</code> &middot; referenced by every page&rsquo;s{' '}
            <code className="px-1.5 py-0.5 rounded text-[13px]" style={{ backgroundColor: '#F0EBE2' }}>og:image</code> tag.
          </p>
        </div>
      </section>

      {/* Platform mockups */}
      <section className="px-6 md:px-12 lg:px-24 py-16" style={{ backgroundColor: '#F0EBE2' }}>
        <div className="max-w-6xl mx-auto">
          <Kicker>Platform previews</Kicker>
          <h2 className="text-2xl md:text-3xl font-normal tracking-tight mb-10" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0F2942' }}>
            How it looks in the wild.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8 items-start">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-semibold mb-4" style={{ color: '#0F2942' }}>WhatsApp</p>
              <WhatsAppMock />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-semibold mb-4" style={{ color: '#0F2942' }}>LinkedIn</p>
              <LinkedInMock />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-semibold mb-4" style={{ color: '#0F2942' }}>iMessage</p>
              <IMessageMock />
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-24 py-16">
        <div className="max-w-3xl mx-auto">
          <Kicker>Verify after deploy</Kicker>
          <h3 className="text-xl md:text-2xl font-normal tracking-tight mb-4" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0F2942' }}>
            Quick checklist
          </h3>
          <ul className="space-y-3 text-base" style={{ color: '#0F2942' }}>
            <li>1. After deploy, visit <a href="https://www.opengraph.xyz/?url=https://culturecareers.org" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: '#D4820A' }}>opengraph.xyz</a> and paste your URL to verify.</li>
            <li>2. On LinkedIn, use the{' '}
              <a href="https://www.linkedin.com/post-inspector/" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: '#D4820A' }}>Post Inspector</a>{' '}
              and hit &ldquo;Inspect&rdquo; to force LinkedIn to refresh its cache.
            </li>
            <li>3. On WhatsApp, send the link in a test chat. The preview appears within 1 to 2 seconds.</li>
          </ul>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default OGCoverPreview;
